import React, { useEffect, useState } from "react";
import useContract from "../hooks/useContract";
import {
  useAppKitAccount,
  useAppKit,
} from "@reown/appkit/react";
import { Contract, formatUnits, JsonRpcProvider } from "ethers";
import {
  KIND_NETWORK_REWARD_CONTRACT_ADDRESS,
  REWARD_NETWORK_KIND_ABI,
} from "../contracts/contracts";
import { toast } from "react-hot-toast";

const RPC_URL = "https://bsc-dataseed.binance.org";

const ERC20_ABI = [
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
];

const Airdrops = () => {
  const { address, isConnected } = useAppKitAccount();
  const { open } = useAppKit();

  const { getUserPackages, getProvider, claimAirdrop } = useContract();

  const [airdrops, setAirdrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [kindPrice, setKindPrice] = useState(0);

  const provider = new JsonRpcProvider(RPC_URL);

  const KIND_ADDRESS = "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b";
  const KIND_BNB_PAIR = "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E";

  const pairAbi = [
    "function getReserves() view returns (uint112,uint112,uint32)",
    "function token0() view returns (address)",
  ];

  // ================= USD HELPER (like Packages page) =================
  const getUSD = (amount) => {
    if (!kindPrice || isNaN(kindPrice) || amount === 0) return "0.00";
    return (Number(amount) * Number(kindPrice)).toFixed(2);
  };

  // ================= KIND PRICE =================
  const fetchKindPrice = async () => {
    try {
      const pair = new Contract(KIND_BNB_PAIR, pairAbi, provider);
      const reserves = await pair.getReserves();
      const token0 = await pair.token0();

      let kindReserve, bnbReserve;
      if (token0.toLowerCase() === KIND_ADDRESS.toLowerCase()) {
        kindReserve = reserves[0];
        bnbReserve = reserves[1];
      } else {
        kindReserve = reserves[1];
        bnbReserve = reserves[0];
      }

      const kindReserveNum = parseFloat(formatUnits(kindReserve, 18));
      const bnbReserveNum = parseFloat(formatUnits(bnbReserve, 18));
      const kindPerBNB = bnbReserveNum / kindReserveNum;

      const CHAINLINK = "0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE";

      const feed = new Contract(
        CHAINLINK,
        [
          "function latestRoundData() view returns (uint80,int256,uint256,uint256,uint80)",
          "function decimals() view returns (uint8)",
        ],
        provider
      );

      const data = await feed.latestRoundData();
      const decimals = await feed.decimals();
      const bnbUsd = parseFloat(formatUnits(data[1], decimals));

      setKindPrice(kindPerBNB * bnbUsd);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchKindPrice();
    const interval = setInterval(fetchKindPrice, 15000);
    return () => clearInterval(interval);
  }, []);

  // ✅ FIXED: ONE BOX PER PID with USD like Packages
  const loadAirdrops = async () => {
    try {
      setLoading(true);

      const signer = await getProvider();
      const contract = new Contract(
        KIND_NETWORK_REWARD_CONTRACT_ADDRESS,
        REWARD_NETWORK_KIND_ABI,
        signer
      );

      const indexes = await getUserPackages();

      // ✅ GROUP BY PID FIRST
      const pidGroups = {};
      for (let i of indexes) {
        const up = await contract.userPackages(i);
        if (!up.active) continue;

        const pid = Number(up.packageId);
        if (!pidGroups[pid]) pidGroups[pid] = [];
        pidGroups[pid].push(Number(i));
      }

      let result = [];

      // ✅ PROCESS EACH UNIQUE PID ONCE
      for (let pidStr of Object.keys(pidGroups)) {
        const pid = Number(pidStr);
        const pkg = await contract.packages(pid);
        
        // ✅ Get ALL airdrop tokens
        const tokens = await contract.getAirdropTokens();
        let tokenList = [];

        // ✅ Aggregate ALL tokens for this PID
        for (let token of tokens) {
          const amount = await contract.pendingAirdropSingle(address, pid, token);
          
          if (amount > 0n) {
            let decimals = 18;
            let symbol = "TOKEN";

            try {
              const tokenContract = new Contract(token, ERC20_ABI, provider);
              decimals = Number(await tokenContract.decimals());
              symbol = await tokenContract.symbol();
            } catch {}

            const realAmount = parseFloat(formatUnits(amount, decimals));
            let usd = 0;

            if (token.toLowerCase() === KIND_ADDRESS.toLowerCase()) {
              usd = realAmount * kindPrice;
            }

            tokenList.push({
              token: token,
              symbol,
              amount: realAmount,
              usd,
            });
          }
        }

        if (tokenList.length > 0) {
          result.push({
            pid,
            name: pkg.name,
            tokens: tokenList,
            packageCount: pidGroups[pidStr].length,
          });
        }
      }

      setAirdrops(result);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (address) {
      loadAirdrops();
    } else {
      setAirdrops([]);
    }
  }, [address, kindPrice]);

  // ================= CLAIM =================
  const handleClaim = async (pid, token) => {
    const t = toast.loading("Processing claim...");

    try {
      setClaiming(token);

      await claimAirdrop(pid, token);

      toast.dismiss(t);
      toast.success("Airdrop claimed successfully! 🎉");

      await loadAirdrops();
      setClaiming(null);
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.message || "Claim failed ❌");
      setClaiming(null);
    }
  };

  // ================= UI =================
  if (!isConnected) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black px-4">
        <div className="w-full max-w-md bg-gradient-to-br from-[#0f1b2d] to-[#1a0f1f] border border-green-500 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <h2 className="text-2xl font-bold text-green-400 mb-2">Connect Wallet</h2>
          <p className="text-gray-400 mb-6 text-sm">Please connect your wallet to view airdrops</p>
          <button
            onClick={() => open()}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all"
          >
            Connect Wallet
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-black">
        <div className="w-14 h-14 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="text-white min-h-screen bg-black p-4">
      {/* HEADER - LIKE PACKAGES */}
      <div className="bg-gradient-to-r from-[#0f1b2d] via-[#0a0f1c] to-[#2a0f2f] p-6 md:p-8 rounded-b-2xl mb-6">
        <h1 className="text-2xl md:text-4xl font-bold text-green-400 mb-3">Airdrops</h1>
        <div className="flex flex-wrap gap-4 text-sm text-gray-300">
          <span>{airdrops.length} Package Types</span>
          <span>•</span>
          <span className="text-yellow-400">KIND: ${kindPrice.toFixed(6) || "Loading..."}</span>
        </div>
      </div>

      {/* RESULTS */}
      {airdrops.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <div className="w-20 h-20 mx-auto mb-4 bg-green-500/10 rounded-2xl flex items-center justify-center">
            🎁
          </div>
          <h3 className="text-xl font-bold mb-2 text-gray-300">No Airdrops Available</h3>
          <p className="text-gray-500">No claimable airdrops found for your active packages.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {airdrops.map((pkg, i) => (
            <div key={i} className="bg-gradient-to-br from-[#0f1b2d] to-[#1a0f1f] border border-gray-700 p-6 rounded-2xl hover:border-green-500 transition-all">
              {/* ✅ HEADER LIKE PACKAGES */}
              <div className="mb-4 p-3 bg-gray-900/50 rounded-xl">
                <p className="text-green-400 font-bold text-lg">
                  {pkg.name}
                </p>
                <p className="text-xs text-gray-400">
                  PID #{pkg.pid} {pkg.packageCount > 1 && `(${pkg.packageCount} packages)`}
                </p>
              </div>

              {/* ✅ TOKENS WITH USD - EXACTLY LIKE PACKAGES */}
              <div className="space-y-4">
                {pkg.tokens.map((t, idx) => (
                  <div key={idx} className="p-4 bg-gray-900/50 rounded-xl border border-gray-700 hover:border-green-500/50 transition-all">
                    {/* ✅ AMOUNT + USD LIKE PACKAGES */}
                    <p className="text-gray-400 mb-2 text-sm">
                      {t.amount.toFixed(4)} {t.symbol}{" "}
                      <span className="text-blue-400 font-semibold">
                        (${t.usd.toFixed(2)})
                      </span>
                    </p>

                    {/* ✅ REWARD CARD STYLE */}
                    <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-xl">
                      <p className="text-green-400 font-bold text-lg">
                        {t.amount.toFixed(4)} {t.symbol}
                      </p>
                      <p className="text-xs text-green-300">
                        ${t.usd.toFixed(2)} USD
                      </p>
                    </div>

                    {/* ✅ CLAIM BUTTON */}
                    <button
                      onClick={() => handleClaim(pkg.pid, t.token)}
                      disabled={claiming === t.token}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3 px-4 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                    >
                      {claiming === t.token 
                        ? "⏳ Claiming..." 
                        : `💰 Claim ${t.symbol}`
                      }
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Airdrops;